import BaseFormatter from './base'
import { SignalVerdict, ExtractedFile } from '../../types'
import {
  AetherPipelineInfo,
  AetherPipelineStep,
  ThreatReputationStepInfo,
  StaticAnalysisStepInfo,
  DynamicAnalysisStepInfo,
  ThreatScoringStepInfo,
  ThreatHuntingStepInfo,
  VerdictCount,
} from '../../features/overview-report/types'
import {
  getOrderedVerdicts,
  getOrderedThreatVerdicts,
  getThreatVerdicts,
  getHigherSeverityVerdict,
} from '../../helpers/signal-type'
import {
  getPredictiveAIVerdict,
  getVerdictFromStatus,
  isDetected,
  PredictiveAIVerdict,
  PREDICTIVE_AI_ENGINE,
  PROVIDER_METADEFENDER,
  PROVIDER_REPUTATION,
} from '../../features/osint/opswat-reputation/helpers'
import { productFeaturesObject, ProductFeature } from 'app/modules/shared'

export class AetherSummaryFormatter extends BaseFormatter {
  static reportSimilarityVerdictThreshold: number = 0

  format(data: { [key: string]: any }, report: { [key: string]: any }): void {
    const aetherPipeline = this.gatherAetherPipelineInfo(data, report)
    if (aetherPipeline && report.overview) {
      report.overview.aetherPipeline = aetherPipeline
    }
  }

  // Gather all info needed for Aether Pipeline
  private gatherAetherPipelineInfo(
    data: { [key: string]: any },
    report: { [key: string]: any }
  ): AetherPipelineInfo | null {
    if (!productFeaturesObject.hasFeature(ProductFeature.Aether)) {
      return null
    }

    const result: AetherPipelineInfo = {
      [AetherPipelineStep.ThreatReputation]: this.gatherThreatReputationInfo(report),
      [AetherPipelineStep.StaticAnalysis]: this.gatherStaticAnalysisInfo(data, report),
      [AetherPipelineStep.DynamicAnalysis]: this.gatherDynamicAnalysisInfo(report),
      [AetherPipelineStep.ThreatScoring]: this.gatherThreatScoringInfo(report),
      [AetherPipelineStep.ThreatHunting]: this.gatherThreatHuntingInfo(report),
    }

    return result
  }

  // Aether Pipeline step 1: ThreatReputation - highest verdict IOCs count
  private gatherThreatReputationInfo(report: { [key: string]: any }): ThreatReputationStepInfo {
    const iocsByVerdict: VerdictCount = {}
    const { mainFile, ...extractedFiles } = report.ioc?.data ?? {}

    if (mainFile) {
      this.countIOCsByVerdict(mainFile, iocsByVerdict)
    }

    for (const fileCategory in extractedFiles) {
      const filesInCategory = extractedFiles[fileCategory]
      for (const fileHash in filesInCategory) {
        this.countIOCsByVerdict(filesInCategory[fileHash], iocsByVerdict)
      }
    }

    const totalIocs = Object.values(iocsByVerdict).reduce((sum, count) => sum + count, 0)
    const riskyIocs = getThreatVerdicts().reduce((sum, verdict) => sum + (iocsByVerdict[verdict] ?? 0), 0)

    let verdict: SignalVerdict = SignalVerdict.Benign
    let iocsCount = totalIocs

    if (riskyIocs > 0) {
      const threatVerdictOrder = getOrderedThreatVerdicts()
      const highestThreatVerdict = threatVerdictOrder.find((v) => (iocsByVerdict[v] ?? 0) > 0)

      if (highestThreatVerdict) {
        verdict = highestThreatVerdict
        iocsCount = iocsByVerdict[highestThreatVerdict]!
      }
    }

    return {
      verdictIocsCount: iocsCount,
      totalIocsCount: totalIocs,
      verdict,
    }
  }

  // Count IOCs by verdict and add to accumulator
  countIOCsByVerdict(iocTypeGroups: { [key: string]: any }, accumulator: VerdictCount): void {
    for (const iocType in iocTypeGroups) {
      const originGroups = iocTypeGroups[iocType]

      if (!Array.isArray(originGroups)) {
        continue
      }

      for (const originGroup of originGroups) {
        if (!originGroup.references || !Array.isArray(originGroup.references)) {
          continue
        }

        for (const ioc of originGroup.references) {
          let verdict: SignalVerdict = (ioc.verdict as SignalVerdict) ?? SignalVerdict.Unknown

          if (!verdict || verdict === SignalVerdict.Unknown) {
            verdict = this.getHighestOsintVerdict(ioc.osintVerdicts ?? []) ?? SignalVerdict.Unknown
          }

          accumulator[verdict] = (accumulator[verdict] || 0) + 1
        }
      }
    }
  }

  // Get the highest severity verdict from osintVerdicts array
  private getHighestOsintVerdict(osintVerdicts: any[]): SignalVerdict | null {
    if (!osintVerdicts || !Array.isArray(osintVerdicts) || osintVerdicts.length === 0) {
      return null
    }

    const verdictOrder = getOrderedVerdicts()

    for (const targetVerdict of verdictOrder) {
      const found = osintVerdicts.find((osintVerdict) => osintVerdict?.verdict?.code === targetVerdict)

      if (found) {
        return targetVerdict
      }
    }

    return null
  }

  // Aether Pipeline step 2: StaticAnalysis - AV engine results + predictive AI
  private gatherStaticAnalysisInfo(
    data: { [key: string]: any },
    report: { [key: string]: any }
  ): StaticAnalysisStepInfo {
    const mainFileHash: string | undefined = report.file?.hash
    const osintResults = this.getPreferredOsintResults(data, mainFileHash)
    const { totalAvs, detectedAvs, avVerdict } = this.gatherAvEngineResults(osintResults)
    const predictiveAiVerdict = this.getPredictiveAiResult(osintResults)

    return {
      predictiveAiVerdict,
      totalAvs,
      detectedAvs,
      avVerdict,
    }
  }

  // Aether Pipeline step 3: DynamicAnalysis - all threat indicators and extracted files
  private gatherDynamicAnalysisInfo(report: { [key: string]: any }): DynamicAnalysisStepInfo {
    const threatIndicatorsByVerdict = this.countThreatIndicatorsByVerdict(report)
    const {
      count: threatIndicatorsCount,
      verdict: threatIndicatorsVerdict,
      total: totalThreatIndicatorsCount,
    } = this.calculateHighestVerdictCount(threatIndicatorsByVerdict)

    const extractedFilesByVerdict: VerdictCount = {}
    const files = report.extracted_files?.files

    if (files) {
      for (const type in files) {
        if (Array.isArray(files[type])) {
          files[type].forEach((file: ExtractedFile) => {
            const verdict = (file.verdict?.verdict as SignalVerdict) ?? SignalVerdict.Unknown
            extractedFilesByVerdict[verdict] = (extractedFilesByVerdict[verdict] ?? 0) + 1
          })
        }
      }
    }

    const {
      count: extractedFilesCount,
      verdict: extractedFilesVerdict,
      total: totalExtractedFilesCount,
    } = this.calculateHighestVerdictCount(extractedFilesByVerdict)

    return {
      verdictThreatIndicatorsCount: threatIndicatorsCount,
      totalThreatIndicatorsCount,
      threatIndicatorsVerdict,
      verdictExtractedFilesCount: extractedFilesCount,
      totalExtractedFilesCount,
      extractedFilesVerdict,
    }
  }

  // Count all threat indicators by verdict
  private countThreatIndicatorsByVerdict(report: { [key: string]: any }): VerdictCount {
    const threatIndicators = report.threatIndicators?.groups ?? {}
    const byVerdict: VerdictCount = {}

    Object.entries(threatIndicators).forEach(([verdict, groups]) => {
      if (groups && Array.isArray(groups)) {
        byVerdict[verdict as SignalVerdict] = (byVerdict[verdict as SignalVerdict] ?? 0) + groups.length
      }
    })

    return byVerdict
  }

  // Helper method to calculate highest risky verdict count alongside the total count
  private calculateHighestVerdictCount(verdictCounts: VerdictCount): {
    count: number
    verdict: SignalVerdict
    total: number
  } {
    const totalCount = Object.values(verdictCounts).reduce((sum, count) => sum + count, 0)
    const riskyCount = getThreatVerdicts().reduce((sum: number, v: SignalVerdict) => sum + (verdictCounts[v] ?? 0), 0)

    if (riskyCount === 0) {
      return { count: totalCount, verdict: SignalVerdict.Benign, total: totalCount }
    }

    const threatVerdictOrder = getOrderedThreatVerdicts()
    const highestThreatVerdict = threatVerdictOrder.find((v) => (verdictCounts[v] ?? 0) > 0)

    return {
      count: verdictCounts[highestThreatVerdict!] ?? 0,
      verdict: highestThreatVerdict ?? SignalVerdict.Benign,
      total: totalCount,
    }
  }

  // Collect OSINT results for the preferred provider that specifically look up the main input
  // file's hash.
  // If OPSWAT MetaDefender has any results, use them over OPSWAT Reputation results, otherwise use Reputation results if present.
  private getPreferredOsintResults(data: { [key: string]: any }, mainFileHash: string | undefined): any[] {
    if (!mainFileHash) {
      return []
    }

    const osintResources = this.getResources(data, 'osint')
    const metadefenderResults: any[] = []
    const reputationResults: any[] = []

    for (const resource of osintResources) {
      if (!Array.isArray(resource.results)) {
        continue
      }

      for (const result of resource.results) {
        if (!this.isMainFileOsintResult(result, mainFileHash)) {
          continue
        }

        if (result?.osintProvider === PROVIDER_METADEFENDER) {
          metadefenderResults.push(result)
        } else if (result?.osintProvider === PROVIDER_REPUTATION) {
          reputationResults.push(result)
        }
      }
    }

    return metadefenderResults.length > 0 ? metadefenderResults : reputationResults
  }

  private isMainFileOsintResult(result: any, mainFileHash: string): boolean {
    const origin = result?.origin
    if (!origin || typeof origin !== 'object') {
      return false
    }

    return origin.type === 'INPUT_FILE' && origin.identifier === mainFileHash && result?.resource === mainFileHash
  }

  // Extract per-result counts and verdict.
  private extractResultCounts(osintResult: any): { total: number; detected: number; verdict: SignalVerdict } {
    const scanResults = osintResult?.data?.scan_results

    // Pre-aggregated MetaDefender numbers — trust and skip iteration.
    if (typeof scanResults?.total_avs === 'number' && typeof scanResults?.scan_all_result_i === 'number') {
      return {
        total: scanResults.total_avs,
        detected: typeof scanResults.total_detected_avs === 'number' ? scanResults.total_detected_avs : 0,
        verdict: getVerdictFromStatus(scanResults.scan_all_result_i),
      }
    }

    const scanDetails = scanResults?.scan_details
    if (scanDetails && typeof scanDetails === 'object') {
      const statuses = Object.values(scanDetails).map((engine: any) =>
        typeof engine?.scan_result_i === 'number' ? engine.scan_result_i : 255
      )
      return this.reduceStatuses(statuses)
    }

    const sources = osintResult?.data?.lookup_results?.sources
    if (Array.isArray(sources)) {
      const statuses = sources.map((source: any) => (typeof source?.status === 'number' ? source.status : 255))
      return this.reduceStatuses(statuses)
    }

    return { total: 0, detected: 0, verdict: SignalVerdict.Benign }
  }

  private reduceStatuses(statuses: number[]): { total: number; detected: number; verdict: SignalVerdict } {
    const total = statuses.length
    const detected = statuses.filter((status) => isDetected(status)).length
    const verdict = statuses.reduce<SignalVerdict>(
      (highest, status) => getHigherSeverityVerdict(highest, getVerdictFromStatus(status)),
      SignalVerdict.Benign
    )

    return { total, detected, verdict }
  }

  // Gather AV engine / reputation scan results aggregated across all preferred-provider OSINT results.
  private gatherAvEngineResults(osintResults: any[]): {
    totalAvs: number
    detectedAvs: number
    avVerdict: SignalVerdict
  } {
    if (osintResults.length === 0) {
      return { totalAvs: 0, detectedAvs: 0, avVerdict: SignalVerdict.Unknown }
    }

    let totalAvs = 0
    let detectedAvs = 0
    let avVerdict: SignalVerdict = SignalVerdict.Benign

    for (const result of osintResults) {
      const counts = this.extractResultCounts(result)
      totalAvs += counts.total
      detectedAvs += counts.detected
      avVerdict = getHigherSeverityVerdict(avVerdict, counts.verdict)
    }

    if (totalAvs === 0) {
      return { totalAvs: 0, detectedAvs: 0, avVerdict: SignalVerdict.Unknown }
    }

    return { totalAvs, detectedAvs, avVerdict }
  }

  // Check predictive AI verdict from the preferred provider's OSINT results, if present.
  private getPredictiveAiResult(osintResults: any[]): PredictiveAIVerdict | undefined {
    for (const result of osintResults) {
      const predictiveEngine = result?.data?.scan_results?.scan_details?.[PREDICTIVE_AI_ENGINE]
      if (predictiveEngine && typeof predictiveEngine === 'object') {
        const status = typeof predictiveEngine.scan_result_i === 'number' ? predictiveEngine.scan_result_i : 255
        return getPredictiveAIVerdict(status)
      }

      const sources = result?.data?.lookup_results?.sources
      if (Array.isArray(sources)) {
        const predictiveSource = sources.find(
          (source: any) => source && typeof source === 'object' && source.provider === PREDICTIVE_AI_ENGINE
        )
        if (predictiveSource) {
          const status = typeof predictiveSource.status === 'number' ? predictiveSource.status : 255
          return getPredictiveAIVerdict(status)
        }
      }
    }

    return
  }

  // Aether Pipeline step 3: ThreatScoring - final report verdict
  private gatherThreatScoringInfo(report: { [key: string]: any }): ThreatScoringStepInfo {
    const verdict = (report.verdict?.name as SignalVerdict) ?? null

    return {
      verdict: verdict,
    }
  }

  // Aether Pipeline step 4: ThreatHunting - similarity count by verdict
  private gatherThreatHuntingInfo(report: { [key: string]: any }): ThreatHuntingStepInfo {
    const similarityResults = report.similaritySearchResults ?? []
    const similarityByVerdict: VerdictCount = {}

    const filteredResults = similarityResults.filter(
      (result: any) => (result?.overall_similarity ?? 0) >= AetherSummaryFormatter.reportSimilarityVerdictThreshold
    )

    filteredResults.forEach((result: any) => {
      const verdict = (result?.details?.verdict as SignalVerdict) ?? SignalVerdict.Unknown
      similarityByVerdict[verdict] = (similarityByVerdict[verdict] ?? 0) + 1
    })

    const totalSimilarity = Object.values(similarityByVerdict).reduce((sum, count) => sum + count, 0)
    const riskySimilarity = getThreatVerdicts().reduce((sum, verdict) => sum + (similarityByVerdict[verdict] ?? 0), 0)

    let verdict: SignalVerdict = SignalVerdict.Benign
    let similarReportsCount = totalSimilarity

    if (riskySimilarity > 0) {
      const threatVerdictOrder = getOrderedThreatVerdicts()
      const highestThreatVerdict = threatVerdictOrder.find((v) => (similarityByVerdict[v] ?? 0) > 0)

      if (highestThreatVerdict) {
        verdict = highestThreatVerdict
        similarReportsCount = similarityByVerdict[highestThreatVerdict]!
      }
    }

    return {
      similarReportsCount,
      verdict,
    }
  }
}
